import * as ImagePicker from 'expo-image-picker';
import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Camera,
  Check,
  ChevronLeft,
  Layers,
  Plus,
  Search,
  Sparkles,
  Upload,
} from 'lucide-react-native';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNotice } from '@/components/ConfirmDialog';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { hapticSuccess } from '@/lib/haptics';
import { scanReceiptBase64 } from '@/lib/ocr';
import { yearBandMidpointYear } from '@/db/persist-onboarding';
import {
  CATEGORY_LABEL,
  SYSTEM_CATALOG,
  type SystemCategory,
  type SystemMeta,
} from '@/data/systems';
import {
  addCustomSystem,
  addSystem,
  getActiveProperty,
  regenerateTasksForProperty,
} from '@/lib/tasks';
import { goBackOrTo } from '@/lib/nav';
import { CUSTOM_TASK_ICONS, ICONS, resolveIcon } from '@/lib/rule-icon';
import { defaultSeasonalRange } from '@/lib/seasonal';
import { webNoOutline } from '@/lib/web-style';
import { StickyBottomBar } from '@/components/StickyBottomBar';

/**
 * Data-plate → system-kind guesser (Wave 64). Keyword-matches the
 * OCR'd plate text against catalog kinds so "point, snap" can also
 * pick the right system, not just the model number. First hit wins —
 * ordered so more specific phrases beat generic ones (dishwasher
 * before washer, heat pump before pump).
 */
const PLATE_KIND_RULES: [RegExp, string][] = [
  [/dish\s?washer/i, 'dw'],
  [/refrigerat|fridge/i, 'fridge'],
  [/washing machine|clothes washer|front.?load washer|top.?load washer/i, 'washer'],
  [/clothes dryer|tumble dryer|\bdryer\b/i, 'dryer'],
  [/water heater|hot water|tankless/i, 'wh'],
  [/heat pump|condensing unit|air condition|\bhvac\b|air handler|furnace/i, 'heatpump'],
  [/mini.?split|ductless/i, 'minisplit'],
  [/boiler/i, 'boiler'],
  [/range|oven|cooktop|stove/i, 'range'],
  [/range hood|vent hood/i, 'hood'],
  [/disposal|disposer/i, 'disp'],
  [/dehumidifier/i, 'dehumid'],
  [/humidifier/i, 'humid'],
  [/water softener/i, 'soft'],
  [/sump pump/i, 'sump'],
  [/generator/i, 'generator'],
  [/thermostat/i, 'thermostat'],
  [/lawn ?mower|riding mower|zero.?turn/i, 'mower'],
  [/snow ?blower|snow ?thrower/i, 'snow_blower'],
  [/pressure washer/i, 'pressure_washer'],
  [/ev charg|wallbox|level 2 charger/i, 'ev_charger'],
  [/garage door opener/i, 'garage_opener'],
];

function guessKindFromPlate(rawText: string): string | null {
  for (const [re, kind] of PLATE_KIND_RULES) {
    if (re.test(rawText) && SYSTEM_CATALOG[kind]) return kind;
  }
  return null;
}

export default function AddSystem() {
  const router = useRouter();
  const db = useSQLiteContext();
  const notice = useNotice();
  const insets = useSafeAreaInsets();
  // Android's gesture nav bar eats taps along the bottom edge — push
  // the sticky "Add all" / single-mode CTA up by the bottom inset so
  // the button stays tappable on phones with on-screen nav.
  const stickyBottom = Math.max(16, insets.bottom + 12);

  const [mode, setMode] = useState<'single' | 'bulk'>('single');

  // Single-mode state
  const [kindKey, setKindKey] = useState<string | null>(null);
  const [subtype, setSubtype] = useState<string | null>(null);
  const [nickname, setNickname] = useState('');
  const [model, setModel] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [installYear, setInstallYear] = useState('');
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  // Tracks whether the user manually edited installYear. Prevents the
  // year-band inheritance effect below from clobbering an explicit
  // entry — important when someone adds an old water heater to a
  // new house.
  const [installYearTouched, setInstallYearTouched] = useState(false);
  /** Default install year inherited from the home's year_band. Lets a
   *  user adding a system to a 1995-built home get installYear=1995 by
   *  default — they only edit it for the OLD water heater in the new
   *  house, not for the typical "everything came with the house" case. */
  const [defaultInstallYear, setDefaultInstallYear] = useState<number | null>(null);

  /** Last system we successfully added in this session — drives the
   *  "Added · X" flash that fades after a couple seconds so the user
   *  knows the save landed but the form clears for the next entry. */
  const [lastAdded, setLastAdded] = useState<string | null>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load the property's year_band → midpoint year on mount. We only
  // apply this default to the input when the user hasn't typed
  // anything yet (installYearTouched stays false until first edit).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const prop = await getActiveProperty(db);
      if (cancelled || !prop) return;
      const y = yearBandMidpointYear(prop.year_band);
      if (y) {
        setDefaultInstallYear(y);
        if (!installYearTouched && !installYear) {
          setInstallYear(String(y));
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db]);

  // Bulk-mode state — count per kind
  const [bulkCounts, setBulkCounts] = useState<Record<string, number>>({});

  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<'all' | 'inside' | 'outside'>('all');
  const [search, setSearch] = useState('');

  // Custom-kind state
  const [customOpen, setCustomOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customIcon, setCustomIcon] = useState('Sparkles');
  const [customLocation, setCustomLocation] = useState<'inside' | 'outside'>('inside');
  const [customCategory, setCustomCategory] = useState<SystemCategory>('amenity');
  const [customIconPickerOpen, setCustomIconPickerOpen] = useState(false);

  const scrollRef = useRef<ScrollView>(null);
  const formAnchorRef = useRef<View>(null);

  const kindMeta: SystemMeta | null = kindKey ? SYSTEM_CATALOG[kindKey] : null;

  const groupedKinds = useMemo(() => {
    const q = search.trim().toLowerCase();
    const groups: Record<string, SystemMeta[]> = {};
    for (const meta of Object.values(SYSTEM_CATALOG)) {
      if (filter !== 'all' && meta.location !== filter) continue;
      if (q) {
        const hay = `${meta.label} ${meta.description} ${meta.category}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      const label = `${meta.location === 'inside' ? 'Inside' : 'Outside'} · ${
        CATEGORY_LABEL[meta.category]
      }`;
      if (!groups[label]) groups[label] = [];
      groups[label].push(meta);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filter, search]);

  const bulkTotal = useMemo(
    () => Object.values(bulkCounts).reduce((sum, n) => sum + n, 0),
    [bulkCounts]
  );

  // When the user picks a kind in single mode, scroll the form into view
  // so they don't have to hunt down the page.
  useEffect(() => {
    if (mode === 'single' && kindKey && formAnchorRef.current && scrollRef.current) {
      formAnchorRef.current.measureLayout(
        // @ts-expect-error — RN's typings here are imprecise but this works.
        scrollRef.current,
        (_x: number, y: number) => {
          scrollRef.current?.scrollTo({ y: Math.max(0, y - 40), animated: true });
        },
        () => {}
      );
    }
  }, [kindKey, mode]);

  const pickPhoto = async () => {
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) return;
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      quality: 0.7,
    });
    if (!res.canceled && res.assets[0]) setPhoto(res.assets[0].uri);
  };

  // "Point. Snap. Done." for systems (Wave 64): photograph the data
  // plate, AI reads it — the system TYPE auto-selects, the model
  // number fills, the serial lands in notes, and the plate photo
  // attaches. The planner then tailors tasks to that exact system.
  const [plateBusy, setPlateBusy] = useState(false);

  const scanPlate = async (source: 'camera' | 'library') => {
    if (plateBusy) return;
    let asset: ImagePicker.ImagePickerAsset | undefined;
    try {
      if (source === 'camera' && Platform.OS !== 'web') {
        const perm = await ImagePicker.requestCameraPermissionsAsync();
        if (!perm.granted) return;
        const res = await ImagePicker.launchCameraAsync({ quality: 0.8, base64: true });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      } else {
        const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (Platform.OS !== 'web' && !perm.granted) return;
        const res = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          quality: 0.8,
          base64: true,
        });
        if (res.canceled || !res.assets[0]) return;
        asset = res.assets[0];
      }
    } catch (err) {
      void notice({ title: 'Camera failed', message: String(err), tone: 'error' });
      return;
    }
    const base64 = asset?.base64 ?? null;
    if (!base64 || !asset) return;
    setPhoto(asset.uri);
    setPlateBusy(true);
    try {
      const result = await scanReceiptBase64(base64);
      if (!result.ok) {
        void notice({
          title: 'Photo attached',
          message: "Couldn't read the plate right now — pick the system below and the photo stays attached.",
          tone: 'info',
        });
        return;
      }
      const parsed = result.parsed;
      if (parsed.model) setModel(parsed.model);
      if (parsed.serial) {
        setNotes((n) => (n ? `${n}\nSerial: ${parsed.serial}` : `Serial: ${parsed.serial}`));
      }
      // Maintenance details straight off the plate (Wave 66): filter
      // size ("16x25x1", "20 X 20 X 1") feeds the filter-swap tasks,
      // and a manufacture date ("MFG DATE 05/2019", "MFG. 2019-05")
      // pre-fills the install year — the two facts the planner
      // actually schedules around.
      const filterMatch = parsed.rawText.match(
        /\b(\d{1,2})\s*[x×]\s*(\d{1,2})\s*[x×]\s*(\d{1,2})\b/i
      );
      if (filterMatch) {
        setFilterSize(`${filterMatch[1]} × ${filterMatch[2]} × ${filterMatch[3]}`);
      }
      const mfgMatch = parsed.rawText.match(
        /\b(?:MFG|MFD|MANUF(?:ACTURED)?|PROD(?:UCTION)?|DOM)\b[^0-9]{0,12}(?:\d{1,2}[\/\-.])?((?:19|20)\d{2})\b/i
      );
      if (mfgMatch) {
        const y = parseInt(mfgMatch[1], 10);
        if (y >= 1950 && y <= new Date().getFullYear()) {
          setInstallYearTouched(true);
          setInstallYear(String(y));
        }
      }
      const kind = guessKindFromPlate(parsed.rawText);
      if (kind) {
        setMode('single');
        setKindKey(kind);
        setSubtype(null);
        hapticSuccess();
        void notice({
          title: `Looks like a ${SYSTEM_CATALOG[kind].label.toLowerCase()}`,
          message:
            (parsed.model ? `Model ${parsed.model} filled in. ` : '') +
            'Check the details below, then add it — your plan tunes to it automatically.',
          tone: 'info',
        });
      } else if (parsed.model || parsed.serial) {
        hapticSuccess();
        void notice({
          title: 'Plate read',
          message: 'Model captured — now pick which system this is below.',
          tone: 'info',
        });
      } else {
        void notice({
          title: 'Photo attached',
          message: "Couldn't spot a model number — pick the system below; the photo stays attached.",
          tone: 'info',
        });
      }
    } finally {
      setPlateBusy(false);
    }
  };

  const saveSingle = async () => {
    if (!kindMeta) return;
    setSaving(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      const seasonal = defaultSeasonalRange(kindMeta.key, prop.climate_zone ?? null);
      const newSystemId = await addSystem(db, {
        propertyId: prop.id,
        kind: kindMeta.key,
        location: kindMeta.location,
        subtype: subtype || null,
        nickname: nickname.trim() || null,
        // Only persist filter_size + model when this kind actually uses them.
        model: (kindMeta.hasModel ?? false) ? model.trim() || null : null,
        filter_size: (kindMeta.hasFilter ?? false) ? filterSize.trim() || null : null,
        installed_year: installYear ? parseInt(installYear, 10) || null : null,
        photo_uri: photo,
        notes: notes.trim() || null,
        is_active: 1,
        active_start_month: seasonal.startMonth,
        active_end_month: seasonal.endMonth,
      });
      await regenerateTasksForProperty(db, prop.id);

      // Land on the system page just created (Wave 48 — user-requested
      // reversal of the old "stay and add another" flow). The detail
      // page is where photos, documents, reminders, and service history
      // live, so arriving there confirms the save AND invites the next
      // step. Its own back arrow / Add-another affordances handle the
      // multi-add case.
      router.replace(`/system/${newSystemId}` as never);
    } catch (err) {
      surfaceError(err);
    } finally {
      setSaving(false);
    }
  };

  const saveCustomKind = async () => {
    const label = customLabel.trim();
    if (!label) return;
    setSaving(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      const newSystemId = await addCustomSystem(db, {
        propertyId: prop.id,
        label,
        icon: customIcon,
        location: customLocation,
        category: customCategory,
      });
      // Same landing as the catalog path — straight to the new system.
      router.replace(`/system/${newSystemId}` as never);
    } catch (err) {
      surfaceError(err);
    } finally {
      setSaving(false);
    }
  };

  const surfaceError = (err: unknown) => {
    const msg = err instanceof Error ? err.message : String(err);
    console.warn('Add system failed', err);
    void notice({ title: "Couldn't save", message: msg, tone: 'error' });
  };

  const saveBulk = async () => {
    if (bulkTotal === 0) return;
    setSaving(true);
    try {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      const entries = Object.entries(bulkCounts).filter(([, n]) => n > 0);
      for (const [key, count] of entries) {
        const meta = SYSTEM_CATALOG[key];
        if (!meta) continue;
        const seasonal = defaultSeasonalRange(meta.key, prop.climate_zone ?? null);
        for (let i = 0; i < count; i++) {
          await addSystem(db, {
            propertyId: prop.id,
            kind: meta.key,
            location: meta.location,
            is_active: 1,
            active_start_month: seasonal.startMonth,
            active_end_month: seasonal.endMonth,
          });
        }
      }
      await regenerateTasksForProperty(db, prop.id);
      goBackOrTo(router, '/(tabs)/my-home');
    } finally {
      setSaving(false);
    }
  };

  const bumpBulk = (key: string, delta: number) => {
    setBulkCounts((prev) => {
      const next = { ...prev };
      const current = next[key] ?? 0;
      const v = Math.max(0, Math.min(99, current + delta));
      if (v === 0) delete next[key];
      else next[key] = v;
      return next;
    });
  };

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable onPress={() => goBackOrTo(router, '/(tabs)/my-home')} style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]} hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Add a system</Text>
        <View style={styles.topbarRight}>
          <Pressable
            onPress={() => setMode(mode === 'single' ? 'bulk' : 'single')}
            style={({ pressed }) => [styles.modeToggle, pressed && { opacity: 0.85 }]}>
            <Layers size={13} color={Palette.forest} strokeWidth={2} />
            <Text style={styles.modeToggleText}>{mode === 'single' ? 'Bulk' : 'Single'}</Text>
          </Pressable>
          {/* Done exits the Add flow back to /systems so the user can
              see everything they just created. Only shown once the
              user has added at least one system this session — before
              that, the existing Back arrow is the natural exit. */}
          {lastAdded ? (
            <Pressable
              onPress={() => goBackOrTo(router, '/systems')}
              style={({ pressed }) => [styles.doneBtn, pressed && { opacity: 0.9 }]}
              accessibilityLabel="Done adding systems">
              <Check size={13} color={Palette.cream} strokeWidth={2.4} />
              <Text style={styles.doneBtnText}>Done</Text>
            </Pressable>
          ) : null}
        </View>
      </View>

      {/* Brief success flash after each save. Lives between the topbar
          and the scroll area so the form clearing is acknowledged
          visibly. Auto-clears via the 2.5s timer set in saveSingle. */}
      {lastAdded ? (
        <View style={styles.flash}>
          <Check size={14} color={Palette.forest} strokeWidth={2.4} />
          <Text style={styles.flashText}>
            Added <Text style={styles.flashEm}>{lastAdded}</Text> — pick the next one
          </Text>
        </View>
      ) : null}

      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{ paddingBottom: 120 + insets.bottom }}
        showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>
          {mode === 'single' ? 'What are we adding?' : 'Add a bunch at once.'}
        </Text>
        <Text style={styles.sub}>
          {mode === 'single'
            ? 'Pick one. Adding details unlocks more tasks tuned to it.'
            : 'Tap +/- to set the count for each. We\'ll create them with defaults — edit later.'}
        </Text>

        {/* Plate scan — the fastest path: AI picks the type, fills the
            model + filter size, and reads the manufacture year (Wave
            64/66). Camera and upload side by side (user ask). */}
        <View style={styles.plateRow}>
          <Pressable
            onPress={() => void scanPlate('camera')}
            disabled={plateBusy || saving}
            style={({ pressed }) => [
              styles.plateBtn,
              { flex: 1.5 },
              pressed && { transform: [{ scale: 0.99 }] },
              plateBusy && { opacity: 0.7 },
            ]}>
            <View style={styles.plateIcon}>
              <Camera size={18} color={Palette.cream} strokeWidth={2} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.plateTitle}>
                {plateBusy ? 'Reading the plate…' : 'Snap the data plate'}
              </Text>
              <Text style={styles.plateSub}>
                AI picks the type + fills model, filter, year.
              </Text>
            </View>
            <Sparkles size={16} color={Palette.honey} strokeWidth={1.8} />
          </Pressable>
          <Pressable
            onPress={() => void scanPlate('library')}
            disabled={plateBusy || saving}
            accessibilityLabel="Upload a photo of the data plate"
            style={({ pressed }) => [
              styles.plateUpload,
              pressed && { transform: [{ scale: 0.98 }] },
              plateBusy && { opacity: 0.7 },
            ]}>
            <Upload size={17} color={Palette.forest} strokeWidth={2} />
            <Text style={styles.plateUploadText}>Upload</Text>
          </Pressable>
        </View>

        <View style={styles.searchBox}>
          <Search size={14} color={Palette.inkFaint} strokeWidth={1.8} />
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search 52 systems (fan, generator, mower)…"
            placeholderTextColor={Palette.inkFaint}
            style={[styles.searchInput, webNoOutline]}
          />
        </View>

        <View style={styles.filterRow}>
          {(['all', 'inside', 'outside'] as const).map((f) => (
            <Pressable
              key={f}
              onPress={() => setFilter(f)}
              style={({ pressed }) => [
                styles.filterChip,
                filter === f && styles.filterChipActive,
                pressed && { opacity: 0.9 },
              ]}>
              <Text style={[styles.filterChipText, filter === f && styles.filterChipTextActive]}>
                {f === 'all' ? 'All' : f === 'inside' ? 'Inside' : 'Outside'}
              </Text>
            </Pressable>
          ))}
        </View>

        <Pressable
          onPress={() => setCustomOpen(true)}
          style={({ pressed }) => [styles.customCta, pressed && { transform: [{ scale: 0.99 }] }]}>
          <Sparkles size={16} color={Palette.honeyDeep} strokeWidth={1.8} />
          <View style={{ flex: 1 }}>
            <Text style={styles.customCtaTitle}>Create a custom system</Text>
            <Text style={styles.customCtaSub}>
              Koi pond? Workshop dust collector? Add your own with a label + icon.
            </Text>
          </View>
          <Plus size={16} color={Palette.honeyDeep} strokeWidth={2} />
        </Pressable>

        {/* Sticky-feel anchor — single-mode form scrolls into view here */}
        {mode === 'single' && kindMeta ? (
          <View ref={formAnchorRef}>
            <FormPanel
              kindMeta={kindMeta}
              subtype={subtype}
              setSubtype={setSubtype}
              nickname={nickname}
              setNickname={setNickname}
              model={model}
              setModel={setModel}
              filterSize={filterSize}
              setFilterSize={setFilterSize}
              installYear={installYear}
              setInstallYear={(v) => {
                setInstallYearTouched(true);
                setInstallYear(v);
              }}
              installYearHint={
                defaultInstallYear && !installYearTouched
                  ? `Pre-filled from your home's build year. Tap to change if this system is older or newer.`
                  : undefined
              }
              notes={notes}
              setNotes={setNotes}
              photo={photo}
              setPhoto={setPhoto}
              pickPhoto={pickPhoto}
              saving={saving}
              onSave={saveSingle}
            />
          </View>
        ) : null}

        {groupedKinds.length === 0 ? (
          <Text style={styles.noResults}>
            No systems match &quot;{search}&quot; — try a broader term.
          </Text>
        ) : null}

        {groupedKinds.map(([label, items]) => (
          <View key={label}>
            <Text style={styles.groupLabel}>{label.toUpperCase()}</Text>
            <View style={styles.kindGrid}>
              {items.map((m) => {
                const Icon = resolveIcon(m.icon);
                if (mode === 'bulk') {
                  const count = bulkCounts[m.key] ?? 0;
                  const selected = count > 0;
                  return (
                    <View
                      key={m.key}
                      style={[styles.kindTile, selected && styles.kindTileSel]}>
                      <View style={[styles.kindIco, selected && styles.kindIcoSel]}>
                        <Icon
                          size={20}
                          color={selected ? Palette.cream : Palette.forest}
                          strokeWidth={1.7}
                        />
                      </View>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.kindLabel} numberOfLines={2}>{m.label}</Text>
                        {count > 1 ? (
                          <Text style={styles.bulkCount}>×{count}</Text>
                        ) : null}
                      </View>
                      <View style={styles.stepper}>
                        <Pressable
                          onPress={() => bumpBulk(m.key, -1)}
                          disabled={count === 0}
                          hitSlop={6}
                          style={({ pressed }) => [styles.stepBtn, pressed && { opacity: 0.6 }]}>
                          <Text style={styles.stepBtnText}>−</Text>
                        </Pressable>
                        <Pressable
                          onPress={() => bumpBulk(m.key, 1)}
                          hitSlop={6}
                          style={({ pressed }) => [styles.stepBtn, pressed && { opacity: 0.6 }]}>
                          <Text style={styles.stepBtnText}>+</Text>
                        </Pressable>
                      </View>
                    </View>
                  );
                }
                const selected = kindKey === m.key;
                return (
                  <Pressable
                    key={m.key}
                    onPress={() => {
                      setKindKey(m.key);
                      setSubtype(null);
                    }}
                    style={({ pressed }) => [
                      styles.kindTile,
                      selected && styles.kindTileSel,
                      pressed && { transform: [{ scale: 0.97 }] },
                    ]}>
                    <View style={[styles.kindIco, selected && styles.kindIcoSel]}>
                      <Icon
                        size={20}
                        color={selected ? Palette.cream : Palette.forest}
                        strokeWidth={1.7}
                      />
                    </View>
                    {/* flex:1 + minWidth:0 lets long labels wrap inside the
                        tile instead of spilling past its border. */}
                    <Text
                      style={[styles.kindLabel, { flex: 1, minWidth: 0 }]}
                      numberOfLines={2}>
                      {m.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        ))}
      </ScrollView>

      {/* Custom-kind builder modal */}
      <CustomKindModal
        open={customOpen}
        label={customLabel}
        setLabel={setCustomLabel}
        icon={customIcon}
        setIcon={setCustomIcon}
        iconPickerOpen={customIconPickerOpen}
        setIconPickerOpen={setCustomIconPickerOpen}
        location={customLocation}
        setLocation={setCustomLocation}
        category={customCategory}
        setCategory={setCustomCategory}
        saving={saving}
        onClose={() => setCustomOpen(false)}
        onSave={saveCustomKind}
      />

      {/* Sticky bulk-mode save bar */}
      {mode === 'bulk' && bulkTotal > 0 ? (
        <View style={[styles.stickyBar, { bottom: stickyBottom }]}>
          <Text style={styles.stickyText}>
            <Text style={styles.stickyEm}>{bulkTotal}</Text> systems queued
          </Text>
          <Pressable
            onPress={saveBulk}
            disabled={saving}
            style={({ pressed }) => [styles.stickyBtn, pressed && { transform: [{ scale: 0.97 }] }]}>
            <Plus size={16} color={Palette.cream} strokeWidth={2.2} />
            <Text style={styles.stickyBtnText}>{saving ? 'Saving…' : 'Add all'}</Text>
          </Pressable>
        </View>
      ) : null}

      {/* Single-mode sticky save — only renders once the user has picked
          a kind. Matches the form pattern used elsewhere (Edit Home,
          Inventory, Expenses) so the primary action never scrolls off. */}
      {mode === 'single' && kindMeta ? (
        <StickyBottomBar>
          <Pressable
            onPress={saveSingle}
            disabled={saving}
            style={({ pressed }) => [
              styles.saveBtn,
              { flex: 1, marginTop: 0 },
              saving && { opacity: 0.6 },
              pressed && { transform: [{ scale: 0.98 }] },
            ]}>
            <Plus size={18} color={Palette.cream} strokeWidth={2} />
            <Text style={styles.saveText}>{saving ? 'Saving…' : `Add ${kindMeta.label}`}</Text>
          </Pressable>
        </StickyBottomBar>
      ) : null}
    </SafeAreaView>
  );
}

function FormPanel(props: {
  kindMeta: SystemMeta;
  subtype: string | null;
  setSubtype: (v: string | null) => void;
  nickname: string;
  setNickname: (v: string) => void;
  model: string;
  setModel: (v: string) => void;
  filterSize: string;
  setFilterSize: (v: string) => void;
  installYear: string;
  setInstallYear: (v: string) => void;
  /** Optional helper line under the install year input. Used by the
   *  parent to surface "pre-filled from your home's build year" when
   *  the value was auto-inherited and the user hasn't touched it. */
  installYearHint?: string;
  notes: string;
  setNotes: (v: string) => void;
  photo: string | null;
  setPhoto: (v: string | null) => void;
  pickPhoto: () => void;
  saving: boolean;
  onSave: () => void;
}) {
  const {
    kindMeta,
    subtype,
    setSubtype,
    nickname,
    setNickname,
    model,
    setModel,
    filterSize,
    setFilterSize,
    installYear,
    setInstallYear,
    installYearHint,
    notes,
    setNotes,
    photo,
    pickPhoto,
    saving,
    onSave,
  } = props;

  return (
    <View style={styles.formCard}>
      <View style={styles.formHead}>
        <Text style={styles.formTitle}>{kindMeta.label}</Text>
        <Text style={styles.formSub}>
          {kindMeta.location === 'inside' ? 'Inside' : 'Outside'} · {CATEGORY_LABEL[kindMeta.category]}
        </Text>
      </View>

      {kindMeta.subtypes && kindMeta.subtypes.length > 0 ? (
        <View style={{ marginTop: Spacing.three }}>
          <Text style={styles.fieldLabel}>WHICH TYPE?</Text>
          <View style={{ gap: 6, marginTop: 6 }}>
            {kindMeta.subtypes.map((s) => {
              const sel = subtype === s.key;
              return (
                <Pressable
                  key={s.key}
                  onPress={() => setSubtype(s.key)}
                  style={({ pressed }) => [
                    styles.subRow,
                    sel && styles.subRowSel,
                    pressed && { opacity: 0.94 },
                  ]}>
                  <View style={[styles.radio, sel && styles.radioSel]}>
                    {sel ? <Check size={11} color={Palette.cream} strokeWidth={2.5} /> : null}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.subTitle}>{s.label}</Text>
                    <Text style={styles.subDesc}>{s.description}</Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}

      <Field label="Nickname" value={nickname} onChangeText={setNickname} placeholder="e.g. Main bedroom fan" />
      {(kindMeta.hasModel ?? false) ? (
        <Field label="Model number" value={model} onChangeText={setModel} placeholder="From the data plate" />
      ) : null}
      {(kindMeta.hasFilter ?? false) ? (
        <Field label="Filter size" value={filterSize} onChangeText={setFilterSize} placeholder="e.g. 16 × 25 × 1" />
      ) : null}
      <Field
        label="Installed (year)"
        value={installYear}
        onChangeText={setInstallYear}
        placeholder="e.g. 2016"
        inputMode="numeric"
        hint={installYearHint}
      />

      <Pressable onPress={pickPhoto} style={styles.photoButton}>
        {photo ? (
          <Image source={{ uri: photo }} style={styles.photo} />
        ) : (
          <>
            <Camera size={22} color={Palette.inkFaint} strokeWidth={1.7} />
            <Text style={styles.photoLabel}>Add a photo (data plate is most useful)</Text>
          </>
        )}
      </Pressable>

      <Text style={styles.fieldLabel}>NOTES</Text>
      <TextInput
        value={notes}
        onChangeText={setNotes}
        placeholder="Anything you want future-you to know about it"
        placeholderTextColor={Palette.inkFaint}
        multiline
        style={[styles.fieldInput, webNoOutline, { height: 80, textAlignVertical: 'top' }]}
      />
      {/* Save lives in the sticky bottom bar — see parent component. The
          inline button used to live here but disappeared off-screen on
          longer forms; sticky keeps it always tappable. */}
    </View>
  );
}

function Field({
  label,
  value,
  onChangeText,
  placeholder,
  inputMode = 'text',
  hint,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
  /** Optional helper line under the input. Renders in muted ink with
   *  a smaller font; used for "auto-filled from your home's year"
   *  context on the install-year field. */
  hint?: string;
}) {
  return (
    <View style={{ marginTop: Spacing.three }}>
      <Text style={styles.fieldLabel}>{label.toUpperCase()}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={Palette.inkFaint}
        inputMode={inputMode}
        style={[styles.fieldInput, webNoOutline]}
      />
      {hint ? <Text style={styles.fieldHint}>{hint}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  modeToggle: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 12, paddingVertical: 7,
    borderRadius: 999, borderWidth: 1.5, borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  modeToggleText: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest, letterSpacing: 0.3,
  },
  // Wraps the right side of the topbar so the mode toggle + Done
  // button can sit side by side without breaking the existing flex
  // distribution (back | title | right cluster).
  topbarRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  doneBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: Palette.forest,
  },
  doneBtnText: {
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    color: Palette.cream,
    letterSpacing: 0.3,
  },
  // "Added · X" success flash that appears between the topbar and the
  // form scroll area for ~2.5s after each save. Honey-soft fill, forest
  // check, mid-weight body — calm enough not to startle, clear enough
  // to read at a glance while the form clears for the next entry.
  flash: {
    marginHorizontal: Spacing.four,
    marginTop: Spacing.two,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 14,
    backgroundColor: 'rgba(225,163,61,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(225,163,61,0.45)',
  },
  flashText: {
    flex: 1,
    fontFamily: Fonts.sansSemibold,
    fontSize: 13,
    color: Palette.ink,
    lineHeight: 18,
  },
  flashEm: {
    fontFamily: Fonts.sansBold,
    color: Palette.forest,
  },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 22, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, fontFamily: Fonts.sansMedium, color: Palette.inkSoft, lineHeight: 19,
  },
  plateBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: Palette.forest,
    borderRadius: 16, padding: 13,
  },
  plateIcon: {
    width: 40, height: 40, borderRadius: 12,
    backgroundColor: 'rgba(251,246,234,0.16)',
    alignItems: 'center', justifyContent: 'center',
  },
  plateRow: {
    flexDirection: 'row', alignItems: 'stretch', gap: 8,
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
  },
  plateUpload: {
    minWidth: 86,
    alignItems: 'center', justifyContent: 'center', gap: 4,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.forest,
    borderRadius: 16, paddingHorizontal: 12,
  },
  plateUploadText: { fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest },
  plateTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.cream },
  plateSub: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium, fontSize: 12.5,
    color: 'rgba(251,246,234,0.75)', lineHeight: 15,
  },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 7,
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    backgroundColor: Palette.card, borderWidth: 1.5,
    borderColor: Palette.line, borderRadius: 11,
    paddingHorizontal: 11,
  },
  searchInput: {
    flex: 1, paddingVertical: 10,
    fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.ink,
  },
  filterRow: {
    flexDirection: 'row', gap: 8,
    paddingHorizontal: Spacing.four, marginTop: Spacing.four,
  },
  filterChip: {
    paddingHorizontal: 14, paddingVertical: 7,
    borderRadius: 999, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.card,
  },
  filterChipActive: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  filterChipText: { fontSize: 13.5, fontFamily: Fonts.sansBold, color: Palette.ink },
  filterChipTextActive: { color: Palette.cream },
  noResults: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft, fontStyle: 'italic',
  },
  groupLabel: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    fontSize: 11.5, fontFamily: Fonts.sansBold, color: Palette.inkFaint, letterSpacing: 0.6,
  },
  kindGrid: {
    flexDirection: 'row', flexWrap: 'wrap', gap: 8,
    paddingHorizontal: Spacing.four, marginTop: 8,
  },
  kindTile: {
    width: '48%', flexDirection: 'row', alignItems: 'center', gap: 9,
    backgroundColor: Palette.card, borderWidth: 1.5, borderColor: Palette.line,
    borderRadius: 14, paddingVertical: 11, paddingHorizontal: 11,
  },
  kindTileSel: {
    borderColor: Palette.forest, backgroundColor: '#EEF3EE',
  },
  kindIco: {
    width: 30, height: 30, borderRadius: 9,
    backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  kindIcoSel: { backgroundColor: Palette.forest },
  kindLabel: {
    fontSize: 13, fontFamily: Fonts.sansSemibold, color: Palette.ink, lineHeight: 15,
  },
  bulkCount: {
    fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.forest, marginTop: 2,
  },
  stepper: { flexDirection: 'row', gap: 3 },
  stepBtn: {
    width: 22, height: 22, borderRadius: 11, backgroundColor: '#EAF0EA',
    alignItems: 'center', justifyContent: 'center',
  },
  stepBtnText: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.forest, lineHeight: 16 },

  formCard: {
    marginTop: Spacing.four, marginHorizontal: Spacing.four,
    backgroundColor: Palette.card, borderWidth: 1.5, borderColor: Palette.forest,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  formHead: { borderBottomWidth: 1, borderBottomColor: Palette.line, paddingBottom: 10 },
  formTitle: { fontFamily: Fonts.serifBold, fontSize: 18, color: Palette.ink },
  formSub: { fontFamily: Fonts.sansSemibold, fontSize: 13, color: Palette.inkSoft, marginTop: 2 },
  fieldLabel: {
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkSoft,
    letterSpacing: 0.4, marginTop: Spacing.three,
  },
  fieldInput: {
    marginTop: 5, borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.sand, borderRadius: 11,
    paddingHorizontal: 12, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14, color: Palette.ink,
  },
  // Helper text shown under an input — used for the install-year
  // auto-fill explanation. Subtle muted ink so it reads as context,
  // not a validation error.
  fieldHint: {
    marginTop: 5,
    fontFamily: Fonts.sansMedium, fontSize: 12.5,
    color: Palette.inkFaint, lineHeight: 16,
    fontStyle: 'italic',
  },
  subRow: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 10,
    padding: 10, backgroundColor: Palette.sand,
    borderRadius: 11, borderWidth: 1.5, borderColor: Palette.line,
  },
  subRowSel: { borderColor: Palette.forest, backgroundColor: '#EEF3EE' },
  radio: {
    width: 20, height: 20, borderRadius: 10,
    borderWidth: 2, borderColor: Palette.sandDeep,
    alignItems: 'center', justifyContent: 'center', marginTop: 1,
  },
  radioSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  subTitle: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  subDesc: {
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    marginTop: 1, lineHeight: 16,
  },
  photoButton: {
    width: '100%', height: 100, marginTop: Spacing.three,
    borderRadius: 14, borderWidth: 2, borderStyle: 'dashed',
    borderColor: Palette.sandDeep, backgroundColor: Palette.sand,
    alignItems: 'center', justifyContent: 'center', gap: 5, overflow: 'hidden',
  },
  photo: { width: '100%', height: '100%', borderRadius: 12 },
  photoLabel: { color: Palette.inkFaint, fontSize: 13, fontFamily: Fonts.sansSemibold },
  saveBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, backgroundColor: Palette.forest,
    paddingVertical: 14, borderRadius: 16, marginTop: Spacing.four,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 14.5 },

  stickyBar: {
    position: 'absolute', left: 14, right: 14, bottom: 16,
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: Palette.forest, borderRadius: 16,
    paddingHorizontal: 18, paddingVertical: 14,
    shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 }, elevation: 6,
  },
  stickyText: { color: Palette.cream, fontFamily: Fonts.sansSemibold, fontSize: 14 },
  stickyEm: { fontFamily: Fonts.sansBold, fontSize: 16 },
  stickyBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: Palette.honey, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 9,
  },
  stickyBtnText: { color: '#3A2A0C', fontFamily: Fonts.sansBold, fontSize: 14 },
  customCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: Spacing.three,
    marginHorizontal: Spacing.four,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 14,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: Palette.honeyDeep,
    backgroundColor: Palette.honeySoft,
  },
  customCtaTitle: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: '#3A2A06' },
  customCtaSub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 12.5,
    color: '#5A4012',
    marginTop: 2,
    lineHeight: 16,
  },
});

const CATEGORY_CHOICES: SystemCategory[] = [
  'mechanical', 'kitchen', 'laundry', 'comfort', 'safety',
  'plumbing', 'fireplace', 'energy', 'grounds', 'structure',
  'amenity', 'equipment',
  // Catch-all bucket for anything that doesn't fit the named
  // categories above. Listed last so it doesn't compete with the
  // primary choices visually but still gives a sensible default for
  // edge cases (workshop, hobby gear, art studio, etc.).
  'other',
];

function CustomKindModal({
  open,
  label,
  setLabel,
  icon,
  setIcon,
  iconPickerOpen,
  setIconPickerOpen,
  location,
  setLocation,
  category,
  setCategory,
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  label: string;
  setLabel: (v: string) => void;
  icon: string;
  setIcon: (v: string) => void;
  iconPickerOpen: boolean;
  setIconPickerOpen: (v: boolean) => void;
  location: 'inside' | 'outside';
  setLocation: (v: 'inside' | 'outside') => void;
  category: SystemCategory;
  setCategory: (v: SystemCategory) => void;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
}) {
  if (!open) return null;
  const IconC = ICONS[icon] ?? ICONS.Sparkles;
  return (
    <Pressable style={customStyles.backdrop} onPress={onClose}>
      <Pressable style={customStyles.modal} onPress={(e) => e.stopPropagation()}>
        <Text style={customStyles.title}>Create a custom system</Text>
        <Text style={customStyles.sub}>
          Give it a name + icon. Add custom tasks to it just like any other system.
        </Text>

        <Pressable
          onPress={() => setIconPickerOpen(true)}
          style={({ pressed }) => [customStyles.iconRow, pressed && { opacity: 0.92 }]}>
          <View style={customStyles.iconBox}>
            <IconC size={22} color={Palette.forest} strokeWidth={1.7} />
          </View>
          <Text style={customStyles.iconLabel}>Tap to change icon</Text>
        </Pressable>

        <Text style={customStyles.field}>NAME</Text>
        <TextInput
          value={label}
          onChangeText={setLabel}
          placeholder="e.g. Koi pond, Workshop dust collector"
          placeholderTextColor={Palette.inkFaint}
          style={[customStyles.input, webNoOutline]}
        />

        <Text style={customStyles.field}>WHERE IS IT?</Text>
        <View style={customStyles.row}>
          {(['inside', 'outside'] as const).map((l) => {
            const sel = location === l;
            return (
              <Pressable
                key={l}
                onPress={() => setLocation(l)}
                style={({ pressed }) => [
                  customStyles.chip,
                  sel && customStyles.chipSel,
                  pressed && { opacity: 0.9 },
                ]}>
                <Text style={[customStyles.chipText, sel && customStyles.chipTextSel]}>
                  {l === 'inside' ? 'Inside' : 'Outside'}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <Text style={customStyles.field}>CATEGORY</Text>
        <View style={[customStyles.row, { flexWrap: 'wrap' }]}>
          {CATEGORY_CHOICES.map((c) => {
            const sel = category === c;
            return (
              <Pressable
                key={c}
                onPress={() => setCategory(c)}
                style={({ pressed }) => [
                  customStyles.chip,
                  sel && customStyles.chipSel,
                  pressed && { opacity: 0.9 },
                ]}>
                <Text style={[customStyles.chipText, sel && customStyles.chipTextSel]}>
                  {CATEGORY_LABEL[c]}
                </Text>
              </Pressable>
            );
          })}
        </View>

        <Pressable
          onPress={onSave}
          disabled={!label.trim() || saving}
          style={({ pressed }) => [
            customStyles.save,
            (!label.trim() || saving) && { opacity: 0.5 },
            pressed && { transform: [{ scale: 0.98 }] },
          ]}>
          <Plus size={16} color={Palette.cream} strokeWidth={2.2} />
          <Text style={customStyles.saveText}>
            {saving ? 'Saving…' : 'Add custom system'}
          </Text>
        </Pressable>

        {/* Icon picker for custom kind */}
        {iconPickerOpen ? (
          <Pressable style={customStyles.backdrop} onPress={() => setIconPickerOpen(false)}>
            <Pressable style={customStyles.iconPicker} onPress={(e) => e.stopPropagation()}>
              <Text style={customStyles.title}>Pick an icon</Text>
              <ScrollView style={{ maxHeight: 380 }}>
                {CUSTOM_TASK_ICONS.map((group) => (
                  <View key={group.group} style={{ marginBottom: 12 }}>
                    <Text style={customStyles.field}>{group.group.toUpperCase()}</Text>
                    <View style={customStyles.iconGrid}>
                      {group.keys
                        .filter((k) => ICONS[k])
                        .map((name) => {
                          const I = ICONS[name];
                          const sel = icon === name;
                          return (
                            <Pressable
                              key={name}
                              onPress={() => {
                                setIcon(name);
                                setIconPickerOpen(false);
                              }}
                              style={({ pressed }) => [
                                customStyles.iconChoice,
                                sel && customStyles.iconChoiceSel,
                                pressed && { opacity: 0.85 },
                              ]}>
                              <I
                                size={20}
                                color={sel ? Palette.cream : Palette.forest}
                                strokeWidth={1.7}
                              />
                            </Pressable>
                          );
                        })}
                    </View>
                  </View>
                ))}
              </ScrollView>
            </Pressable>
          </Pressable>
        ) : null}
      </Pressable>
    </Pressable>
  );
}

const customStyles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center',
    padding: 20,
    zIndex: 999,
  },
  modal: {
    backgroundColor: Palette.sand,
    borderRadius: 24,
    padding: 20,
    width: '100%',
    maxWidth: 420,
  },
  title: {
    fontFamily: Fonts.serifBold,
    fontSize: 18,
    color: Palette.ink,
    marginBottom: 4,
  },
  sub: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
    marginBottom: 14,
    lineHeight: 17,
  },
  iconRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    backgroundColor: Palette.card,
    borderRadius: 11,
    borderWidth: 1.5,
    borderColor: Palette.line,
    marginBottom: 6,
  },
  iconBox: {
    width: 36,
    height: 36,
    borderRadius: 11,
    backgroundColor: '#EAF0EA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconLabel: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  field: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkSoft,
    letterSpacing: 0.4,
    marginTop: 10,
    marginBottom: 4,
  },
  input: {
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    borderRadius: 11,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontFamily: Fonts.sansSemibold,
    fontSize: 14,
    color: Palette.ink,
  },
  row: { flexDirection: 'row', gap: 6 },
  chip: {
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: Palette.line,
    backgroundColor: Palette.card,
    marginBottom: 5,
  },
  chipSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
  chipText: { fontFamily: Fonts.sansBold, fontSize: 13, color: Palette.ink },
  chipTextSel: { color: Palette.cream },
  save: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: Palette.forest,
    paddingVertical: 14,
    borderRadius: 14,
    marginTop: 14,
  },
  saveText: { color: Palette.cream, fontFamily: Fonts.sansBold, fontSize: 14.5 },
  iconPicker: {
    backgroundColor: Palette.sand,
    borderRadius: 24,
    padding: 20,
    width: '100%',
    maxWidth: 420,
  },
  iconGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  iconChoice: {
    width: 40,
    height: 40,
    borderRadius: 11,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconChoiceSel: { backgroundColor: Palette.forest, borderColor: Palette.forest },
});
